/**
 * Review AI-proposed ownership events before any of them is written.
 *
 * The step that makes this safe is RESOLUTION. `enrichParentOrgOwnershipEvents`
 * returns a company NAME — a model cannot know a document slug — so every row
 * has to name a real catalogue entry before it can be applied. A row that
 * resolves cleanly starts ticked; one that matched nothing, or matched two
 * companies with the same name, starts blocked with a picker, and Apply simply
 * cannot include it.
 *
 * Events the organization already records (same date, same target) are shown as
 * duplicates and start unticked, because the prompt asks the model to EXTEND but
 * a re-run after a partial apply will legitimately re-propose what is stored.
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import type { ParentOrganization, ParentOrgOwnershipEvent } from '../../types';
import {
  enrichParentOrgOwnershipEventsWithAI,
  type AIOwnershipEvent,
} from '../../services/firebase';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { EntityPicker, optionMatches, type EntityOption } from './EntityPicker';
import { QuickOrgResolve } from './QuickOrgResolve';
import {
  resolveOwnershipEvents,
  toStoredEvent,
  type ResolveCatalogues,
  type ResolvedOwnershipEvent,
} from './ownershipEventResolve';

/** A fresh id for an applied event, matching the section's own generator. */
function newEventId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `evt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

interface Props {
  isOpen: boolean;
  org: ParentOrganization;
  catalogues: ResolveCatalogues;
  /** Option lists for the manual pickers, keyed the same way. */
  options: Record<'brand' | 'manufacturer' | 'parentOrg', EntityOption[]>;
  /** Every parent organization as a full document — what quick-create and add-as-AKA act on. */
  orgs: ParentOrganization[];
  onClose: () => void;
  /** Called with the events to append. The section owns the write. */
  onApply: (events: ParentOrgOwnershipEvent[]) => Promise<void>;
  /** An organization was created or gained an alias here — hosts should reload. */
  onOrgsChanged?: () => void;
}

export function OwnershipEnrichDialog({
  isOpen,
  org,
  catalogues,
  options,
  orgs,
  onClose,
  onApply,
  onOrgsChanged,
}: Props) {
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [raw, setRaw] = useState<AIOwnershipEvent[] | null>(null);
  const [dropped, setDropped] = useState(0);
  const [context, setContext] = useState('');
  const [accepted, setAccepted] = useState<Record<number, boolean>>({});
  /** Targets the curator picked by hand for rows that did not resolve. */
  const [manualTargets, setManualTargets] = useState<Record<number, string>>({});
  const [pickerQuery, setPickerQuery] = useState<Record<number, string>>({});
  /** Previous owners the curator resolved by quick-create or add-as-AKA. */
  const [previousOwnerPicks, setPreviousOwnerPicks] = useState<Record<number, string>>({});
  /**
   * Organizations created, or given a new alias, during this review — laid over
   * the catalogue the host passed in.
   *
   * The host's `orgs` is a snapshot from when the editor opened, and its reload
   * after `onOrgsChanged` arrives whenever it arrives. Without the overlay, the
   * next row naming the same company would still show "no match" until then,
   * and the curator would quick-create it twice. With it, every row resolves
   * against the new document the moment the write returns.
   */
  const [sessionOrgs, setSessionOrgs] = useState<Record<string, ParentOrganization>>({});

  useEffect(() => {
    if (isOpen) return;
    setRaw(null);
    setAccepted({});
    setManualTargets({});
    setPickerQuery({});
    setPreviousOwnerPicks({});
    setSessionOrgs({});
    setContext('');
  }, [isOpen]);

  /** The host's organizations with this session's writes laid over them by id. */
  const effectiveOrgs = useMemo(() => {
    const byId = new Map(orgs.map((o) => [o.id, o]));
    for (const o of Object.values(sessionOrgs)) byId.set(o.id, o);
    return [...byId.values()];
  }, [orgs, sessionOrgs]);

  const effectiveCatalogues: ResolveCatalogues = useMemo(
    () => (Object.keys(sessionOrgs).length ? { ...catalogues, parentOrg: effectiveOrgs } : catalogues),
    [catalogues, effectiveOrgs, sessionOrgs],
  );

  const effectiveOptions = useMemo(
    () =>
      Object.keys(sessionOrgs).length
        ? {
            ...options,
            parentOrg: effectiveOrgs.map((o) => ({
              id: o.id,
              label: o.displayName || o.name || o.id,
              sub: o.country,
            })),
          }
        : options,
    [options, effectiveOrgs, sessionOrgs],
  );

  const resolved: ResolvedOwnershipEvent[] = useMemo(
    () => (raw ? resolveOwnershipEvents(raw, effectiveCatalogues, org.ownershipEvents ?? []) : []),
    [raw, effectiveCatalogues, org.ownershipEvents],
  );

  /** Record a quick-create or new alias, so every row resolves against it at once. */
  const rememberOrg = (next: ParentOrganization) => {
    setSessionOrgs((prev) => ({ ...prev, [next.id]: next }));
    onOrgsChanged?.();
  };

  /** The previous owner actually in force for a row: the curator's fix wins. */
  const previousOwnerIdFor = (index: number): string | null =>
    previousOwnerPicks[index] || resolved[index]?.previousOwner?.id || null;

  /** The target actually in force for a row: the curator's pick wins. */
  const targetIdFor = (index: number): string | null =>
    manualTargets[index] || resolved[index]?.target.id || null;

  const handleRun = async () => {
    setRunning(true);
    try {
      const result = await enrichParentOrgOwnershipEventsWithAI({
        parentOrgId: org.id,
        ...(context.trim() ? { additionalContext: [context.trim()] } : {}),
      });
      setRaw(result.events);
      setDropped(result.dropped);
      const next = resolveOwnershipEvents(
        result.events,
        effectiveCatalogues,
        org.ownershipEvents ?? [],
      );
      // Ticked only when it resolved AND is not already recorded — the two
      // conditions under which applying it is unambiguously right.
      setAccepted(
        Object.fromEntries(next.map((r, i) => [i, r.target.id !== null && !r.duplicate])),
      );
      if (result.events.length === 0) toast('No events proposed');
    } catch (err) {
      console.error('Ownership-event enrichment failed:', err);
      toast.error(err instanceof Error ? err.message : 'AI enrichment failed');
    } finally {
      setRunning(false);
    }
  };

  const applicable = resolved
    .map((r, i) => ({ r, i }))
    .filter(({ i }) => accepted[i] && targetIdFor(i));

  const handleApply = async () => {
    const events = applicable
      .map(({ r, i }) =>
        toStoredEvent(r, newEventId(), {
          targetId: targetIdFor(i) ?? undefined,
          previousOwnerId: previousOwnerIdFor(i) ?? undefined,
        }),
      )
      .filter((e): e is ParentOrgOwnershipEvent => e !== null);
    if (events.length === 0) return;
    setApplying(true);
    try {
      await onApply(events);
      toast.success(`Added ${events.length} event${events.length === 1 ? '' : 's'}`);
      onClose();
    } catch (err) {
      console.error('Failed to apply ownership events:', err);
      toast.error('Could not save those events.');
    } finally {
      setApplying(false);
    }
  };

  const busy = running || applying;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => (busy ? undefined : onClose())}
      title={`AI ownership events — ${org.displayName || org.name}`}
      size="full"
    >
      <div className="space-y-4">
        {!raw && (
          <>
            <p className="text-sm text-gray-600">
              Proposes acquisitions, divestitures, absorptions, foundings and renamings for this
              organization, up to 40. Each one names a company, which is matched against the
              catalogue here — nothing is written until you apply.
            </p>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Additional context <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <textarea
                value={context}
                aria-label="Additional context"
                onChange={(e) => setContext(e.target.value)}
                rows={3}
                placeholder="Paste research the model should treat as ground truth…"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </>
        )}

        {raw && (
          <p className="text-sm text-gray-600">
            {raw.length} proposed{dropped > 0 && `, ${dropped} dropped as unusable`}.{' '}
            <span className="text-amber-700">
              A row that matched no company, or matched two, can&rsquo;t be applied until you pick
              its target.
            </span>
          </p>
        )}

        {raw && raw.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="w-10 px-3 py-2" />
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Target</th>
                  <th className="px-3 py-2">Previous owner</th>
                  <th className="px-3 py-2">Confidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {resolved.map((row, i) => {
                  const targetId = targetIdFor(i);
                  const blocked = !targetId;
                  return (
                    <tr
                      key={i}
                      className={
                        blocked ? 'bg-amber-50/60' : row.duplicate ? 'bg-gray-50' : undefined
                      }
                    >
                      <td className="px-3 py-2 align-top">
                        <input
                          type="checkbox"
                          aria-label={`Accept event ${i + 1}`}
                          checked={!!accepted[i] && !blocked}
                          disabled={blocked || busy}
                          onChange={(e) =>
                            setAccepted((prev) => ({ ...prev, [i]: e.target.checked }))
                          }
                        />
                      </td>
                      <td className="px-3 py-2 align-top font-mono text-xs text-gray-700">
                        {row.event.date}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-indigo-700">
                          {row.event.type}
                        </span>
                        <div className="text-[10px] text-gray-400">{row.event.targetType}</div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="text-gray-800">{row.event.targetName}</div>
                        {row.target.via === 'exact' && !manualTargets[i] && (
                          <div className="text-[11px] text-emerald-700">
                            ✓ matched {row.target.id}
                          </div>
                        )}
                        {row.duplicate && (
                          <div className="text-[11px] text-gray-500">
                            Already recorded — unticked
                          </div>
                        )}
                        {blocked && (
                          <div className="mt-1">
                            <div className="mb-1 text-[11px] text-amber-800">
                              {row.target.via === 'ambiguous'
                                ? `${row.target.candidateIds.length} companies share this name — pick one`
                                : 'No catalogue match — pick the company'}
                            </div>
                            <EntityPicker
                              value={manualTargets[i] ?? ''}
                              options={(effectiveOptions[row.event.targetType] ?? []).filter((o) =>
                                optionMatches(o, pickerQuery[i] ?? ''),
                              )}
                              query={pickerQuery[i] ?? ''}
                              onQueryChange={(q) =>
                                setPickerQuery((prev) => ({ ...prev, [i]: q }))
                              }
                              onSelect={(id) =>
                                setManualTargets((prev) => ({ ...prev, [i]: id }))
                              }
                              ariaLabel={`Target for event ${i + 1}`}
                              placeholder={`Search ${row.event.targetType}s…`}
                              allowClear
                            />
                            {/* Parent-org targets only. A brand or manufacturer
                                target is picked from its catalogue — never minted
                                here, where a model-proposed name would too easily
                                become a parent organization called "Blancpain". */}
                            {row.event.targetType === 'parentOrg' && (
                              <QuickOrgResolve
                                name={row.event.targetName}
                                orgs={effectiveOrgs}
                                disabled={busy}
                                ariaContext={`event ${i + 1} target`}
                                onResolved={(next) => {
                                  rememberOrg(next);
                                  setManualTargets((prev) => ({ ...prev, [i]: next.id }));
                                }}
                              />
                            )}
                          </div>
                        )}
                        {row.event.description && (
                          <p className="mt-1 max-w-md text-xs text-gray-600">
                            {row.event.description}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top text-xs text-gray-600">
                        {row.event.previousOwnerName ? (
                          <>
                            {row.event.previousOwnerName}
                            {previousOwnerIdFor(i) ? (
                              previousOwnerPicks[i] && (
                                <div className="text-[11px] text-emerald-700">
                                  ✓ {previousOwnerPicks[i]}
                                </div>
                              )
                            ) : (
                              <>
                                <div className="text-[11px] text-gray-400">
                                  no match — will be left blank
                                </div>
                                <QuickOrgResolve
                                  name={row.event.previousOwnerName}
                                  orgs={effectiveOrgs}
                                  disabled={busy}
                                  ariaContext={`event ${i + 1} previous owner`}
                                  onResolved={(next) => {
                                    rememberOrg(next);
                                    setPreviousOwnerPicks((prev) => ({ ...prev, [i]: next.id }));
                                  }}
                                />
                              </>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top text-xs text-gray-600">
                        {Math.round((row.event.aiConfidence ?? 0) * 100)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Close
          </Button>
          {!raw ? (
            <Button onClick={() => void handleRun()} loading={running}>
              Propose events
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => void handleRun()} disabled={busy}>
                Re-run
              </Button>
              <Button
                onClick={() => void handleApply()}
                loading={applying}
                disabled={applicable.length === 0 || busy}
              >
                Add {applicable.length} event{applicable.length === 1 ? '' : 's'}
              </Button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
